/**
 * Componente de Breadcrumbs (Navegação hierárquica)
 * 
 * @author Equipe GestaoeCRM
 * @date 19/12/2025
 */

import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/utils/cn';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

/**
 * Mapeia rotas para labels de breadcrumb
 */
const routeLabels: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/clients': 'Clientes',
  '/projects': 'Projetos',
  '/demands': 'Demandas',
  '/os': 'Jobs/Tasks',
  '/reports': 'Relatórios',
  '/administration': 'Administração',
  '/administration/users': 'Usuários',
  '/administration/company-areas': 'Áreas da Empresa',
  '/administration/hour-rates': 'Taxas por Hora',
  '/administration/permissions': 'Permissões',
  '/administration/tenants': 'Empresas',
  '/administration/sla': 'SLA',
  '/administration/admin-spreadsheet': 'Planilha Administrativa',
  '/administration/spreadsheet': 'Planilha Administrativa',
  '/daily-scrum': 'Daily Scrum',
  '/checklist': 'Checklist',
  '/plans': 'Planos',
  '/my-plan': 'Meu Plano',
  '/usage-and-limits': 'Uso e Limites',
  '/notifications': 'Notificações',
  '/payment/success': 'Confirmação de Pagamento',
  '/register': 'Registro',
};

/**
 * Padrões de rotas dinâmicas e seus labels
 */
const dynamicRoutePatterns: Array<{ pattern: RegExp; label: string; parentPath: string }> = [
  { pattern: /^\/clients\/([^/]+)$/, label: 'Detalhes do Cliente', parentPath: '/clients' },
  { pattern: /^\/projects\/([^/]+)$/, label: 'Detalhes do Projeto', parentPath: '/projects' },
  { pattern: /^\/demands\/([^/]+)$/, label: 'Detalhes da Demanda', parentPath: '/demands' },
  { pattern: /^\/demands\/([^/]+)\/checklist$/, label: 'Checklist', parentPath: '/demands' },
  { pattern: /^\/demands\/([^/]+)\/jobs-tasks$/, label: 'Jobs/Tasks', parentPath: '/demands' },
  { pattern: /^\/os\/([^/]+)$/, label: 'Detalhes do Job/Task', parentPath: '/os' },
  { pattern: /^\/administration\/company-areas\/([^/]+)$/, label: 'Detalhes da Área', parentPath: '/administration/company-areas' },
  { pattern: /^\/administration\/company-areas\/transfer$/, label: 'Transferência de Área', parentPath: '/administration/company-areas' },
  { pattern: /^\/reports\/os-by-area$/, label: 'OS por Área', parentPath: '/reports' },
  { pattern: /^\/reports\/os-completed$/, label: 'OS Concluídas', parentPath: '/reports' },
];

/**
 * Extrai breadcrumbs da rota atual
 */
function getBreadcrumbsFromPath(pathname: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [
    { label: 'Início', href: '/dashboard', icon: Home },
  ];

  // Remove query params e hash
  const cleanPath = pathname.split('?')[0].split('#')[0];
  
  // Se já está na home, retorna apenas ela
  if (cleanPath === '/dashboard' || cleanPath === '/') {
    return items;
  }

  // Verifica se é uma rota dinâmica conhecida
  for (const { pattern, label, parentPath } of dynamicRoutePatterns) {
    const match = cleanPath.match(pattern);
    if (match) {
      // Adiciona o parent path se existir no mapeamento
      if (parentPath && routeLabels[parentPath]) {
        items.push({
          label: routeLabels[parentPath],
          href: parentPath,
        });
      }
      // Adiciona o item atual
      items.push({
        label,
        href: undefined, // Último item não tem href
      });
      return items;
    }
  }

  // Divide o path em partes para rotas estáticas
  const pathParts = cleanPath.split('/').filter(Boolean);
  
  // Constrói breadcrumbs incrementalmente
  let currentPath = '';
  pathParts.forEach((part, index) => {
    currentPath += `/${part}`;
    
    // Tenta obter label do mapeamento
    const label = routeLabels[currentPath] || 
                  part.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    
    // Último item não tem href (está na página atual)
    const isLast = index === pathParts.length - 1;
    
    items.push({
      label,
      href: isLast ? undefined : currentPath,
    });
  });

  return items;
}

export default function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const location = useLocation();
  
  // Usa items customizados se fornecidos, senão gera da rota
  const breadcrumbItems = items || getBreadcrumbsFromPath(location.pathname);

  if (breadcrumbItems.length <= 1) {
    return null; // Não mostra breadcrumb se só tem "Início"
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-text-secondary overflow-x-auto scrollbar-hide',
        className
      )}
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const Icon = item.icon;

        return (
          <div key={index} className="flex items-center space-x-2">
            {index > 0 && (
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-text-muted flex-shrink-0" />
            )}
            
            {isLast ? (
              <span className="flex items-center space-x-1 text-text-primary font-medium flex-shrink-0">
                {Icon && <Icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />}
                <span className="truncate max-w-[150px] sm:max-w-none">{item.label}</span>
              </span>
            ) : (
              <Link
                to={item.href || '#'}
                className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200 flex-shrink-0"
              >
                {Icon && <Icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />}
                <span className="truncate max-w-[100px] sm:max-w-none">{item.label}</span>
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}

